import { source_text_from_data } from "./banana_progress_gauge_core_base.js";
import {
  color_layer_definitions_from_data,
  normalise_layer_colors_for_definitions,
  svg_layer_id_from_name,
  svg_layer_name_from_node,
} from "./banana_progress_gauge_core_colors.js";

function clean_imported_svg_node (pNode)
  {
    if (!pNode || !pNode.parentNode) return;
    const tTag = (pNode.tagName || "").toLowerCase ();
    if (["script", "foreignobject"].includes (tTag))
      {
        pNode.parentNode.removeChild (pNode);
        return;
      }
    if (typeof pNode.removeAttribute === "function")
      {
        Array.from (pNode.attributes || []).forEach ((pAttr) =>
          {
            const tName = (pAttr.name || "").toLowerCase ();
            if (tName.startsWith ("on")) pNode.removeAttribute (pAttr.name);
          });
      }
    if (typeof pNode.querySelectorAll === "function")
      Array.from (pNode.querySelectorAll ("*")).forEach (clean_imported_svg_node);
  }

function prefix_imported_svg_ids (pRoot, pPrefix)
  {
    if (!pRoot || typeof pRoot.querySelectorAll !== "function") return;
    const tMap = new Map ();
    [pRoot].concat (Array.from (pRoot.querySelectorAll ("[id]"))).forEach ((pNode) =>
      {
        const tId = pNode.getAttribute && pNode.getAttribute ("id");
        if (tId && !tMap.has (tId)) tMap.set (tId, `${pPrefix}_${tId}`);
      });
    if (!tMap.size) return;
    const remplace = (pValue) =>
      {
        let tValue = pValue;
        tMap.forEach ((pNew, pOld) =>
          {
            const tEsc = pOld.replace (/[.*+?^${}()|[\]\\]/g, "\\$&");
            tValue = tValue
              .replace (new RegExp (`url\\(#${tEsc}\\)`, "g"), `url(#${pNew})`)
              .replace (new RegExp (`#${tEsc}\\b`, "g"), `#${pNew}`);
          });
        return tValue;
      };
    [pRoot].concat (Array.from (pRoot.querySelectorAll ("*"))).forEach ((pNode) =>
      {
        const tId = pNode.getAttribute && pNode.getAttribute ("id");
        if (tId && tMap.has (tId)) pNode.setAttribute ("id", tMap.get (tId));
        Array.from (pNode.attributes || []).forEach ((pAttr) =>
          {
            if (!pAttr || pAttr.name === "id") return;
            const tValue = remplace (pAttr.value || "");
            if (tValue !== pAttr.value) pNode.setAttribute (pAttr.name, tValue);
          });
        if ((pNode.tagName || "").toLowerCase () === "style")
          pNode.textContent = remplace (pNode.textContent || "");
      });
  }

function prepare_imported_svg_layers (pRoot)
  {
    if (!pRoot || typeof pRoot.querySelectorAll !== "function") return;
    const tUsedIds = new Set ();
    Array.from (pRoot.querySelectorAll ("path, polygon, polyline, rect, circle, ellipse, line, text, use"))
      .forEach ((pNode, pIndex) =>
        {
          const tBaseId = svg_layer_id_from_name (svg_layer_name_from_node (pNode, pIndex), pIndex);
          let tId = tBaseId;
          let tSuffix = 2;
          while (tUsedIds.has (tId))
            {
              tId = `${tBaseId}_${tSuffix}`;
              tSuffix += 1;
            }
          tUsedIds.add (tId);
          pNode.setAttribute ("data-banana-layer", tId);
          pNode.style.pointerEvents = "visiblePainted";
        });
  }

export function inject_source_svg (pTemplate)
  {
    const tHost = pTemplate && pTemplate.find && pTemplate.find (".js_banana_source_inline");
    if (!tHost) return;
    const tText = source_text_from_data (pTemplate.data);
    const tKey = `${tText.length}:${tText.slice (0, 80)}`;
    if (pTemplate.bananaSourceSvgKey === tKey) return;
    pTemplate.bananaSourceSvgKey = tKey;
    while (tHost.firstChild) tHost.removeChild (tHost.firstChild);
    if (!tText || typeof DOMParser === "undefined") return;
    const ajoute_phase = (pPhase, pClipId = "") =>
      {
        const tImportDocument = tHost.ownerDocument || document;
        const tGroup = tImportDocument.createElementNS ("http://www.w3.org/2000/svg", "g");
        tGroup.setAttribute ("class", `banana-progress-gauge_source-phase banana-progress-gauge_source-phase-${pPhase}`);
        tGroup.setAttribute ("data-banana-progress-phase", pPhase);
        if (pClipId) tGroup.setAttribute ("clip-path", `url(#${pClipId})`);
        const tDoc = new DOMParser ().parseFromString (tText, "image/svg+xml");
        const tSvg = tDoc && tDoc.documentElement;
        const tErreur = tDoc && typeof tDoc.querySelector === "function"
          ? tDoc.querySelector ("parsererror")
          : null;
        if (!tSvg || tSvg.nodeName.toLowerCase () !== "svg" || tErreur) return null;
        clean_imported_svg_node (tSvg);
        prefix_imported_svg_ids (tSvg, `banana_src_${pTemplate.bananaProgressGaugeClipId || Date.now ()}_${pPhase}`);
        prepare_imported_svg_layers (tSvg);
        Array.from (tSvg.childNodes || []).forEach ((pChild) =>
          {
            tGroup.appendChild (tImportDocument.importNode (pChild, true));
          });
        tHost.appendChild (tGroup);
        return tGroup;
      };
    try
      {
        ajoute_phase ("start");
        ajoute_phase ("end", pTemplate.bananaProgressGaugeClipId);
      }
    catch (e) {}
  }

export function apply_imported_svg_layer_styles (pTemplate, pData)
  {
    const tHost = pTemplate && pTemplate.find && pTemplate.find (".js_banana_source_inline");
    if (!tHost || typeof tHost.querySelectorAll !== "function") return;
    const tColors = normalise_layer_colors_for_definitions (
      (pData && pData.layerColors) || (pData && pData.config && pData.config.layerColors),
      color_layer_definitions_from_data (pData)
    );
    const tSelected = pData && pData.selectedColorLayer;
    Array.from (tHost.querySelectorAll ("[data-banana-layer]")).forEach ((pNode) =>
      {
        const tLayerId = pNode.getAttribute ("data-banana-layer");
        const tLayerColors = tColors[tLayerId];
        if (tLayerColors)
          {
            const tPhaseNode = pNode.closest
              ? pNode.closest ("[data-banana-progress-phase]")
              : null;
            const tPhase = tPhaseNode && tPhaseNode.getAttribute ("data-banana-progress-phase") === "end"
              ? "end"
              : "start";
            const tColor = tPhase === "end" ? tLayerColors.end : tLayerColors.start;
            pNode.setAttribute ("fill", tColor);
            if (pNode.style) pNode.style.fill = tColor;
          }
        if (tLayerId && tLayerId === tSelected) pNode.classList.add ("is-color-selected");
        else pNode.classList.remove ("is-color-selected");
      });
  }
